import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../api";
import { Copy, Check, ArrowLeft } from "lucide-react";

const ROOT_DOMAIN = process.env.REACT_APP_PLATFORM_ROOT_DOMAIN || "filagestao.com.br";

function slugify(v) {
  return (v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9-\s]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const SEGMENTS = [
  "Salão de beleza", "Esmalteria", "Barbearia", "Clínica",
  "Estética", "Studio", "Outros",
];

export default function SuperTenantCreate() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", display_name: "", tv_display_name: "", slug: "",
    segment: "",
    email: "", whatsapp: "", city: "", state: "",
    logo_url: "",
    master_name: "", master_email: "", master_password: "",
  });
  const [autoSlug, setAutoSlug] = useState(true);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onChangeName = (v) => {
    set("name", v);
    if (autoSlug) set("slug", slugify(v));
    if (!form.display_name) set("display_name", v);
    if (!form.tv_display_name) set("tv_display_name", v);
  };

  const onSlugChange = (v) => { setAutoSlug(false); set("slug", slugify(v)); };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error("Envie PNG, JPG ou WEBP");
      return;
    }
    if (f.size > 600 * 1024) {
      toast.error("Logo grande demais (máx 600KB)");
      return;
    }
    const d = await readFileAsDataURL(f);
    set("logo_url", d);
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "display_name", "tv_display_name", "slug", "segment", "email", "master_name", "master_email", "master_password"];
    for (const k of required) if (!form[k]) { toast.error(`Preencha ${k}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/super/tenants", { ...form, active: true });
      setCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const copyUrl = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.access_url);
      setCopied(true);
      toast.success("Endereço copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Não foi possível copiar"); }
  };

  if (created) {
    return (
      <div className="max-w-xl mx-auto space-y-6" data-testid="super-tenant-success">
        <div className="rounded-2xl border border-brand-200 bg-white p-8 shadow-lg shadow-brand-500/10">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-white">
            <Check size={24} />
          </div>
          <h2 className="text-2xl font-semibold text-brand-600">CLIENTE CRIADO COM SUCESSO</h2>
          <dl className="mt-6 grid grid-cols-1 gap-4 text-sm">
            <div>
              <dt className="text-[11px] uppercase tracking-widest text-zinc-500">Nome</dt>
              <dd className="mt-1 font-medium text-zinc-900" data-testid="success-name">{created.tenant.name}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-widest text-zinc-500">Acesso</dt>
              <dd className="mt-1">
                <a href={created.access_url} target="_blank" rel="noreferrer" className="font-mono text-brand-600 hover:underline" data-testid="success-access-url">
                  {created.access_url}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-widest text-zinc-500">Master</dt>
              <dd className="mt-1 font-medium text-zinc-900" data-testid="success-master-email">{created.master.email}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-widest text-zinc-500">Status</dt>
              <dd className="mt-1">
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-100 px-2 py-1 text-[11px] font-semibold text-brand-700">Ativo</span>
              </dd>
            </div>
          </dl>
          <div className="mt-8 flex flex-wrap gap-2">
            <button
              data-testid="btn-copy-access-url"
              onClick={copyUrl}
              className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copiado!" : "Copiar endereço de acesso"}
            </button>
            <button
              onClick={() => nav("/super/clientes")}
              className="rounded-full border border-zinc-200 bg-white px-5 py-2 text-sm font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-600"
            >Voltar aos clientes</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-brand-600">
        <ArrowLeft size={14} /> Voltar
      </button>
      <h1 className="text-2xl font-semibold">Novo Cliente</h1>

      <form onSubmit={submit} data-testid="super-tenant-form" className="space-y-8">
        <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-500 mb-4">Empresa</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome da empresa *" testid="f-name">
              <input value={form.name} onChange={(e) => onChangeName(e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Nome de exibição *" testid="f-display">
              <input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Nome exibido no Painel TV *" testid="f-tv">
              <input value={form.tv_display_name} onChange={(e) => set("tv_display_name", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Slug *" testid="f-slug" help={form.slug ? `https://${ROOT_DOMAIN}/${form.slug}` : ""}>
              <input value={form.slug} onChange={(e) => onSlugChange(e.target.value)} required pattern="[a-z0-9][a-z0-9-]*" className={inputCls} placeholder="ex: lerose" />
            </Field>
            <Field label="Segmento *" testid="f-segment">
              <select value={form.segment} onChange={(e) => set("segment", e.target.value)} required className={inputCls}>
                <option value="">Selecione…</option>
                {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="E-mail do responsável *" testid="f-email">
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="WhatsApp" testid="f-whatsapp">
              <input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Cidade" testid="f-city">
              <input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Estado" testid="f-state">
              <input value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} maxLength={2} />
            </Field>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">Logo</label>
            <div className="flex items-center gap-4">
              {form.logo_url && <img src={form.logo_url} alt="preview" className="h-14 w-14 rounded-full object-cover border border-zinc-200" />}
              <input data-testid="f-logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo}
                className="text-sm text-zinc-600 file:mr-3 file:rounded-full file:border-0 file:bg-brand-500 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-brand-600" />
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-500 mb-4">Usuário Master</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome *" testid="f-master-name">
              <input value={form.master_name} onChange={(e) => set("master_name", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="E-mail *" testid="f-master-email">
              <input type="email" value={form.master_email} onChange={(e) => set("master_email", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Senha provisória *" testid="f-master-password">
              <input type="password" minLength={4} value={form.master_password} onChange={(e) => set("master_password", e.target.value)} required className={inputCls} />
            </Field>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            data-testid="super-tenant-submit"
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 disabled:opacity-60 transition-all"
          >
            {busy ? "Criando…" : "Criar cliente"}
          </button>
          <button type="button" onClick={() => nav("/super/clientes")} className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-700 hover:border-brand-300 hover:text-brand-600">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all";

function Field({ label, testid, help, children }) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">{label}</label>
      <div data-testid={testid}>{children}</div>
      {help && <p className="mt-1 text-[11px] font-mono text-zinc-500 truncate">{help}</p>}
    </div>
  );
}
